import { useMemo } from 'react';
import { createIdempotencyKey } from './idempotency';
import { getAdminEnv, getAuthHeaders, useAdminConfig } from './adminConfig';
import type {
  AdminApiError,
  AdminCheckoutAttemptRead,
  AdminCheckoutRead,
  AdminDemoRead,
  ApproveLaunchRequest,
  ApproveLaunchResponse,
  BackendCreateCheckoutRequest,
  BackendApproveLaunchResponse,
  BackendCreateCheckoutResponse,
  BackendCreateLaunchRequest,
  BackendCreateLaunchResponse,
  BackendDisableAccessCodeResponse,
  BackendEnableAccessCodeResponse,
  BackendLaunchDetail,
  BackendLaunchListItem,
  BackendLaunchListResponse,
  CreateCheckoutRequest,
  BackendRotateAccessCodeResponse,
  CreateCheckoutResponse,
  CreateLaunchRequest,
  CreateLaunchResponse,
  DisableAccessCodeResponse,
  EnableAccessCodeResponse,
  LaunchDetail,
  LaunchListItem,
  PaginatedLaunchList,
  PaymentEvidence,
  RotateAccessCodeResponse,
  SubscriptionDetail,
} from '../features/clinicLaunches/types';

const API_TIMEOUT_MS = 30_000;

export interface AdminApi {
  listLaunches(params: { limit: number; offset: number }): Promise<PaginatedLaunchList>;
  getLaunch(launchId: string): Promise<LaunchDetail>;
  createLaunch(request: CreateLaunchRequest, idempotencyKey: string): Promise<CreateLaunchResponse>;
  approveLaunch(
    launchId: string,
    request: ApproveLaunchRequest,
    idempotencyKey: string,
  ): Promise<ApproveLaunchResponse>;
  createCheckout(
    launchId: string,
    idempotencyKey: string,
    request?: CreateCheckoutRequest,
  ): Promise<CreateCheckoutResponse>;
  rotateAccessCode(launchId: string, idempotencyKey: string): Promise<RotateAccessCodeResponse>;
  disableAccessCode(launchId: string, idempotencyKey: string): Promise<DisableAccessCodeResponse>;
  enableAccessCode(launchId: string, idempotencyKey: string): Promise<EnableAccessCodeResponse>;
}

export function createAdminApi(config: ReturnType<typeof useAdminConfig>): AdminApi {
  const baseUrl = config.apiBaseUrl.replace(/\/$/, '');
  const authHeaders = getAuthHeaders(config);

  async function request<T>(
    path: string,
    options: RequestInit & { skipAuth?: boolean } = {},
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...authHeaders,
      ...(options.headers as Record<string, string> | undefined),
    };

    const url = `${baseUrl}${path}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers,
        signal: controller.signal,
      });

      if (response.status === 403 || response.status === 503) {
        throw classifyError(response.status, 'Check Cloudflare Access, CORS, and API configuration.');
      }

      if (response.status === 404) {
        const body = await response.text().catch(() => '');
        throw classifyNotFound(path, body);
      }

      if (!response.ok) {
        const text = await response.text().catch(() => 'Request failed');
        const parsed = tryParseErrorBody(text);
        throw classifyError(response.status, text, parsed?.code);
      }

      return (await response.json()) as T;
    } catch (error) {
      if (error instanceof AdminApiErrorImpl) {
        throw error;
      }
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw classifyError(undefined, 'Request timed out. Please retry.');
      }
      throw classifyError(undefined, error instanceof Error ? error.message : 'Network error');
    } finally {
      clearTimeout(timeoutId);
    }
  }

  return {
    listLaunches: async ({ limit, offset }) => {
      const path = `/api/v1/admin/clinic-launches?limit=${limit}&offset=${offset}`;
      const raw = await request<BackendLaunchListResponse>(path, { method: 'GET' });
      return {
        items: raw.items.map(normalizeListItem),
        total: raw.total,
        limit: raw.limit,
        offset: raw.offset,
      };
    },

    getLaunch: async (launchId) => {
      const path = `/api/v1/admin/clinic-launches/${encodeURIComponent(launchId)}`;
      const raw = await request<BackendLaunchDetail>(path, { method: 'GET' });
      return normalizeDetail(raw);
    },

    createLaunch: async (payload, idempotencyKey) => {
      const body: BackendCreateLaunchRequest = {
        slug: payload.slug,
        name: payload.name,
        contact_email: payload.contact_email,
        billing_interval: payload.billing_interval,
        launch_options: payload.launch_options,
      };
      const path = '/api/v1/admin/clinic-launches';
      const raw = await request<BackendCreateLaunchResponse>(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CanisX-Admin-Intent': 'true',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify(body),
      });
      return normalizeCreateResponse(raw);
    },

    approveLaunch: async (launchId, payload, idempotencyKey) => {
      const path = `/api/v1/admin/clinic-launches/${encodeURIComponent(launchId)}/approve`;
      const raw = await request<BackendApproveLaunchResponse>(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CanisX-Admin-Intent': 'true',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify(payload),
      });
      return normalizeApproveResponse(raw);
    },

    createCheckout: async (launchId, idempotencyKey, payload = {}) => {
      const body: BackendCreateCheckoutRequest = payload.regenerate ? { regenerate: true } : {};
      const path = `/api/v1/admin/clinic-launches/${encodeURIComponent(launchId)}/checkout`;
      const raw = await request<BackendCreateCheckoutResponse>(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CanisX-Admin-Intent': 'true',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify(body),
      });
      return normalizeCreateCheckoutResponse(raw);
    },

    rotateAccessCode: async (launchId, idempotencyKey) => {
      const path = `/api/v1/admin/clinic-launches/${encodeURIComponent(launchId)}/demo/code/rotate`;
      const raw = await request<BackendRotateAccessCodeResponse>(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CanisX-Admin-Intent': 'true',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({}),
      });
      return normalizeRotateAccessCodeResponse(raw, launchId);
    },

    disableAccessCode: async (launchId, idempotencyKey) => {
      const path = `/api/v1/admin/clinic-launches/${encodeURIComponent(launchId)}/demo/code/disable`;
      const raw = await request<BackendDisableAccessCodeResponse>(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CanisX-Admin-Intent': 'true',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({}),
      });
      return normalizeDisableAccessCodeResponse(raw, launchId);
    },

    enableAccessCode: async (launchId, idempotencyKey) => {
      const path = `/api/v1/admin/clinic-launches/${encodeURIComponent(launchId)}/demo/code/enable`;
      const raw = await request<BackendEnableAccessCodeResponse>(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CanisX-Admin-Intent': 'true',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({}),
      });
      return normalizeEnableAccessCodeResponse(raw, launchId);
    },
  };
}

export function useAdminApi(): AdminApi {
  const config = useAdminConfig();
  return useMemo(
    () => createAdminApi(config),
    // Config values are static per build; these primitives keep the client stable.
    [config.apiBaseUrl, config.authMode, config.localToken],
  );
}

class AdminApiErrorImpl extends Error implements AdminApiError {
  type: AdminApiError['type'];
  recoverable: boolean;
  code?: string;
  status?: number;

  constructor(type: AdminApiError['type'], message: string, recoverable = true, code?: string, status?: number) {
    super(message);
    this.type = type;
    this.recoverable = recoverable;
    this.code = code;
    this.status = status;
    this.name = 'AdminApiError';
  }
}

export function getBillingRefusalMessage(code?: string): string | undefined {
  switch (code) {
    case 'collection_not_qualified':
      return 'Payment collection is not qualified for this agreement. Paid activation remains blocked.';
    case 'subscription_reactivation_requires_operator':
      return 'This clinic has a linked subscription. Operator review is required before reactivation.';
    default:
      return undefined;
  }
}

export function classifyError(status: number | undefined, message: string, code?: string): AdminApiError {
  switch (status) {
    case 403:
      return new AdminApiErrorImpl(
        'auth',
        'Access denied. Verify Cloudflare Access or admin credentials.',
        true,
        code,
        status,
      );
    case 404:
      return new AdminApiErrorImpl(
        'config',
        'Admin API returned 404. Check the base URL and endpoint path.',
        true,
        code,
        status,
      );
    case 405:
    case 501:
      return new AdminApiErrorImpl(
        'config',
        'Admin API endpoint is not available or misconfigured. Check the base URL and deployment.',
        true,
        code,
        status,
      );
    case 503:
      return new AdminApiErrorImpl(
        'config',
        'Admin API is unreachable or misconfigured. Check CORS, base URL, and Cloudflare Access.',
        true,
        code,
        status,
      );
    case 409: {
      const refusalMessage = getBillingRefusalMessage(code);
      if (refusalMessage) {
        return new AdminApiErrorImpl(
          'server',
          refusalMessage,
          false,
          code,
          status,
        );
      }
      return new AdminApiErrorImpl(
        'server',
        'Launch state changed. Please refresh the latest detail and try again.',
        true,
        code,
        status,
      );
    }
    case 422:
      // Never embed the raw backend response body here (R15). Only the
      // parsed `code` (see tryParseErrorBody) is safe to carry forward;
      // callers that know a specific 422 `code` (e.g. create-launch's
      // clinic_contact_email_required) can render more specific typed copy
      // themselves without touching backend-authored text.
      return new AdminApiErrorImpl(
        'server',
        'The admin API rejected the request. Check the submitted fields and try again.',
        true,
        code,
        status,
      );
    case 429:
      // Same discipline as 422: never surface raw backend text for a
      // lockout/rate-limit response (R15).
      return new AdminApiErrorImpl(
        'server',
        'Too many requests or failed attempts. Wait a moment before trying again.',
        true,
        code,
        status,
      );
    case 500:
    case 502:
    case 504:
      return new AdminApiErrorImpl('server', 'Admin API error. Try again shortly.', true, code, status);
    default:
      if (status !== undefined) {
        // Any other backend-classified HTTP status: discard raw backend body
        // text at the API boundary (R15, R3) rather than surfacing it via
        // `message`. Callers may still branch on `status`/`code` for
        // route-specific safe copy.
        return new AdminApiErrorImpl(
          'server',
          'Admin API rejected the request. Try again or contact support if it persists.',
          true,
          code,
          status,
        );
      }
      // status === undefined only occurs for local/network conditions
      // (request timeout, fetch/network failure) whose `message` is always a
      // developer-authored safe string from this file, never raw backend
      // response text.
      return new AdminApiErrorImpl(
        'network',
        message || 'Network error. Please retry.',
        true,
        code,
        status,
      );
  }
}

function classifyNotFound(path: string, body: string): AdminApiError {
  const isLaunchPath = path.includes('/clinic-launches/');
  // Only treat explicit launch_not_found code as a missing launch. Generic
  // "not found" text (e.g. from a route-missing 404) should not clear detail.
  let parsed: { code?: string; error?: string; detail?: { code?: string } } | null = null;
  try {
    parsed = JSON.parse(body);
  } catch {
    parsed = null;
  }
  const code = parsed?.detail?.code ?? parsed?.code ?? parsed?.error;
  if (isLaunchPath && code === 'launch_not_found') {
    return new AdminApiErrorImpl(
      'not-found',
      'Launch not found. It may have been removed or the ID is invalid.',
      true,
    );
  }
  return new AdminApiErrorImpl(
    'config',
    'Admin API is unreachable or misconfigured. Check CORS, base URL, and Cloudflare Access.',
    true,
  );
}

export function isAdminApiError(error: unknown): error is AdminApiError {
  return error instanceof AdminApiErrorImpl;
}

export function isNotFoundError(error: unknown): error is AdminApiError {
  return isAdminApiError(error) && error.type === 'not-found';
}

// Audited for R15: this must only ever extract the machine-readable `code`
// string. Do not widen this to return other backend body fields (message,
// detail text, access codes, etc.) — those are discarded at the API boundary
// and must never reach `AdminApiError`.
function tryParseErrorBody(text: string): { code?: string } | null {
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed.code === 'string') {
      return { code: parsed.code };
    }
    if (parsed?.detail && typeof parsed.detail.code === 'string') {
      return { code: parsed.detail.code };
    }
    return null;
  } catch {
    return null;
  }
}

export function sanitizeDisplayMessage(message: string): string {
  return message.replace(/https?:\/\/[^\s"'`]+/g, '[redacted]');
}

function normalizeListItem(raw: BackendLaunchListItem): LaunchListItem {
  return {
    launch_id: raw.launch_id,
    slug: raw.slug,
    name: raw.name,
    status: raw.launch_status,
    next_action: raw.next_action,
    billing_interval: raw.billing_interval,
    approved_at: raw.approved_at,
  };
}

// Accepted stable-demo-URL hosts by adminpanel environment (R4): the dev
// deployment only accepts the dev clinic host, the prod deployment only
// accepts the prod clinic host, and local/preview builds — which may point
// at either backend depending on configuration — accept both. Backend-
// returned stable URLs are copied verbatim (never synthesized from slug),
// but must be validated against this allowlist before being treated as safe
// to display or share.
const STABLE_URL_HOST_SUFFIXES_BY_ENV: Record<ReturnType<typeof getAdminEnv>, string[]> = {
  prod: ['.canisxclinic.com'],
  dev: ['.trycanisx.com'],
  local: ['.trycanisx.com', '.canisxclinic.com'],
};

// Accepted one-time checkout URL hosts. Only real production payment
// providers — Stripe and Dodo Payments — are accepted. Test-style hosts
// such as `pay.example.com` (RFC 2606 reserved) or `pay.link` are NOT
// accepted: they are fixture hosts and have no role in production
// one-time checkout URLs. Anything else is treated as backend-not-ready
// and dropped rather than displayed (R4, TS7).
const ONE_TIME_CHECKOUT_ALLOWED_HOST_SUFFIXES = [
  'checkout.stripe.com',
  '.dodopayments.com',
];

// Strict allowlist of known checkout statuses. Unknown backend statuses are
// normalized to 'unknown' rather than passed through raw with underscores
// replaced — prevents rendering arbitrary backend strings in the UI.
const CHECKOUT_STATUS_ALLOWLIST = new Set([
  'created',
  'superseded',
  'expired',
  'failed',
  'pending',
]);

// Provider-specific allowed query parameter shapes for one-time checkout URLs.
// Stripe uses: cs_test_xxx (in path), client_secret, session_id, redirect_url
// Dodo Payments: minimal query params
// Rejects all nonempty query strings until exact Dodo/Stripe fixtures are known
// (TS7 tighten). Safe fallback: no query params are allowed on checkout URLs.
const ONE_TIME_CHECKOUT_ALLOWED_QUERY_KEYS = new Set<string>();
const STRIPE_CHECKOUT_PATH_PATTERN = /^\/c\/pay\/cs_(test|live)_[A-Za-z0-9_=-]+$/;
const DODO_CHECKOUT_PATH_PATTERN = /^\/(?:buy\/pdt_|session\/cks_)[A-Za-z0-9_=-]+$/;

/**
 * Returns the stable demo URL only if it is a plain HTTPS URL with no
 * fragment, query string, or embedded credentials, and its host matches an
 * accepted suffix for the current adminpanel environment. Anything else
 * (malformed, legacy fragment-token shape, unexpected host, or a host that
 * belongs to a different environment) is treated as backend-not-ready and
 * dropped rather than displayed (R4, TS7).
 */
export function normalizeStableDemoUrl(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  if (url.protocol !== 'https:') return undefined;
  if (url.hash || url.search || url.username || url.password) return undefined;
  const allowedSuffixes = STABLE_URL_HOST_SUFFIXES_BY_ENV[getAdminEnv()];
  if (!allowedSuffixes.some((suffix) => url.hostname.endsWith(suffix))) return undefined;
  return raw;
}

export function normalizeLegacyDemoUrl(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  if (url.protocol !== 'https:') return undefined;
  if (!url.hash || url.search || url.username || url.password) return undefined;
  const allowedSuffixes = STABLE_URL_HOST_SUFFIXES_BY_ENV[getAdminEnv()];
  if (!allowedSuffixes.some((suffix) => url.hostname.endsWith(suffix))) return undefined;
  const fragment = url.hash.slice(1);
  if (!fragment || !/^[A-Za-z0-9._~=-]+$/.test(fragment)) return undefined;
  return raw;
}

export function normalizeOneTimeCheckoutUrl(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  if (url.protocol !== 'https:') return undefined;
  if (url.hash || url.username || url.password) return undefined;
  const isAllowedHost = ONE_TIME_CHECKOUT_ALLOWED_HOST_SUFFIXES.some((suffix) =>
    suffix.startsWith('.') ? url.hostname.endsWith(suffix) : url.hostname === suffix,
  );
  if (!isAllowedHost) return undefined;
  if (url.hostname === 'checkout.stripe.com' && !STRIPE_CHECKOUT_PATH_PATTERN.test(url.pathname)) {
    return undefined;
  }
  if (url.hostname.endsWith('.dodopayments.com') && !DODO_CHECKOUT_PATH_PATTERN.test(url.pathname)) {
    return undefined;
  }
  // Reject any query string until exact provider fixtures are known. Currently
  // no production checkout URLs carry query parameters — this is the safest
  // default and can be relaxed per-provider once fixtures are established.
  for (const key of url.searchParams.keys()) {
    if (!ONE_TIME_CHECKOUT_ALLOWED_QUERY_KEYS.has(key.toLowerCase())) {
      return undefined;
    }
  }
  return raw;
}

function normalizeDemoRead(raw: AdminDemoRead | null | undefined): LaunchDetail['demo'] {
  if (!raw) return undefined;
  return {
    demo_id: raw.demo_id,
    demo_email: raw.demo_email,
    activated_at: raw.activated_at,
    expires_at: raw.expires_at,
    access_disabled: raw.access_disabled,
    staff_user_id: raw.staff_user_id,
    stable_demo_url: normalizeStableDemoUrl(raw.stable_demo_url),
    access_code_rotated_at: raw.access_code_rotated_at,
    access_code_disabled_at: raw.access_code_disabled_at,
    access_code_version: raw.access_code_version,
    failed_login_count: raw.failed_login_count,
    locked_until: raw.locked_until,
  };
}

function normalizeCheckoutRead(raw: AdminCheckoutRead | null | undefined): LaunchDetail['checkout'] {
  if (!raw) return undefined;
  return {
    checkout_attempt_id: raw.checkout_attempt_id,
    status: CHECKOUT_STATUS_ALLOWLIST.has(raw.status) ? raw.status : 'unknown',
    expires_at: raw.expires_at,
  };
}

function normalizeCheckoutAttemptHistory(
  raw: AdminCheckoutAttemptRead[] | null | undefined,
): LaunchDetail['checkout_attempts'] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return undefined;
  }
  const items = raw
    .filter(
      (attempt): attempt is AdminCheckoutAttemptRead =>
        Boolean(attempt?.checkout_attempt_id) && Boolean(attempt?.status),
    )
    .map((attempt) => ({
      checkout_attempt_id: attempt.checkout_attempt_id,
      status: CHECKOUT_STATUS_ALLOWLIST.has(attempt.status) ? attempt.status : 'unknown',
      created_at: attempt.created_at,
      updated_at: attempt.updated_at,
      expires_at: attempt.expires_at,
      superseded_at: attempt.superseded_at,
      superseded_by_attempt_id: attempt.superseded_by_attempt_id,
    }));
  return items.length > 0 ? items : undefined;
}

function normalizeSubscription(
  raw: BackendLaunchDetail['subscription'],
): SubscriptionDetail | undefined {
  if (!raw) return undefined;
  return {
    subscription_id: raw.subscription_id,
    status: raw.status,
    billing_interval: raw.billing_interval,
    dodo_subscription_id: raw.dodo_subscription_id,
    period_start: raw.period_start,
    period_end: raw.period_end,
    paid_through: raw.paid_through,
    billing_agreement: raw.billing_agreement,
  };
}

function normalizePayment(raw: BackendLaunchDetail['recent_payment']): PaymentEvidence | undefined {
  if (!raw) return undefined;
  return {
    payment_event_id: raw.payment_event_id,
    event_type: raw.event_type,
    status: raw.status,
    amount_cents: raw.amount_cents,
    currency: raw.currency,
    occurred_at: raw.occurred_at,
  };
}

function normalizeDetail(raw: BackendLaunchDetail): LaunchDetail {
  return {
    launch_id: raw.launch_id,
    clinic_id: raw.clinic_id,
    slug: raw.slug,
    name: raw.name,
    contact_email: raw.contact_email,
    status: raw.launch_status,
    next_action: raw.next_action,
    public_branding_enabled: raw.public_branding_enabled,
    demo: normalizeDemoRead(raw.demo),
    checkout: normalizeCheckoutRead(raw.checkout),
    checkout_attempts: normalizeCheckoutAttemptHistory(raw.checkout_attempts),
    subscription: normalizeSubscription(raw.subscription),
    recent_payment: normalizePayment(raw.recent_payment),
    paid_enrollment_enabled: raw.paid_enrollment_enabled,
    approved_at: raw.approved_at,
    approved_by_email: raw.approved_by_email,
    billing_interval: raw.billing_interval,
    billing_agreement: raw.billing_agreement,
    offered_billing_agreement: raw.offered_billing_agreement,
  };
}

function normalizeOneTimeDemo(
  raw: BackendCreateLaunchResponse['demo'],
): CreateLaunchResponse['demo'] | undefined {
  if (!raw) return undefined;
  const stableUrl = normalizeStableDemoUrl(raw.stable_demo_url);
  return {
    // Legacy fragment-token URL is only surfaced when no valid stable URL is
    // present, preserving the existing one-time flow during backend rollout.
    // It must never be promoted into stable_demo_url (TS6).
    demo_url: stableUrl ? undefined : normalizeLegacyDemoUrl(raw.demo_url),
    demo_expires_at: raw.expires_at,
    stable_demo_url: stableUrl,
    demo_access_code: raw.demo_access_code,
  };
}

function normalizeOneTimeCheckout(
  raw: BackendCreateLaunchResponse['checkout'],
): CreateLaunchResponse['checkout'] | undefined {
  if (!raw) return undefined;
  return {
    checkout_url: normalizeOneTimeCheckoutUrl(raw.checkout_url),
    checkout_attempt_id: raw.checkout_attempt_id,
    checkout_expires_at: raw.expires_at,
  };
}

function normalizeCreateResponse(raw: BackendCreateLaunchResponse): CreateLaunchResponse {
  return {
    launch_id: raw.launch_id,
    status: raw.launch_status,
    paid_enrollment_enabled: raw.paid_enrollment_enabled,
    public_branding_enabled: raw.public_branding_enabled,
    offered_billing_agreement: raw.offered_billing_agreement,
    demo: normalizeOneTimeDemo(raw.demo) ?? {},
    checkout: normalizeOneTimeCheckout(raw.checkout),
  };
}

function normalizeApproveResponse(raw: BackendApproveLaunchResponse): ApproveLaunchResponse {
  return {
    launch_id: raw.launch_id,
    status: raw.launch_status,
    paid_enrollment_enabled: raw.paid_enrollment_enabled,
    public_branding_enabled: raw.public_branding_enabled,
  };
}

function normalizeCreateCheckoutResponse(raw: BackendCreateCheckoutResponse): CreateCheckoutResponse {
  return {
    launch_id: raw.launch_id,
    checkout_url: normalizeOneTimeCheckoutUrl(raw.checkout?.checkout_url),
    checkout_attempt_id: raw.checkout?.checkout_attempt_id,
    checkout_expires_at: raw.checkout?.expires_at,
  };
}

// Never fabricate a code: `demo_access_code` is normalized field-by-field and
// stays undefined whenever the backend omits it (ambiguous outcomes such as
// timeout, replay-without-code, or conflict) rather than defaulting to a
// placeholder (R10, KTD6). `launch_id` comes from the request argument, not
// the backend body — the real backend response (AdminDemoResponse) has no
// launch_id field, so trusting it from `raw` would always be undefined.
function normalizeRotateAccessCodeResponse(
  raw: BackendRotateAccessCodeResponse,
  launchId: string,
): RotateAccessCodeResponse {
  return {
    launch_id: launchId,
    demo_access_code: raw.demo_access_code,
    access_code_rotated_at: raw.access_code_rotated_at,
  };
}

// Disable/enable never carry a raw credential (R9/R14) — only the launch id
// (from the request argument, same reasoning as rotate above) and the
// durable access_code_disabled_at signal are normalized. Callers treat these
// as confirmation of the mutation and refresh durable detail/list for the
// authoritative state rather than trusting this response alone.
function normalizeDisableAccessCodeResponse(
  raw: BackendDisableAccessCodeResponse,
  launchId: string,
): DisableAccessCodeResponse {
  return {
    launch_id: launchId,
    access_code_disabled_at: raw.access_code_disabled_at,
  };
}

function normalizeEnableAccessCodeResponse(
  raw: BackendEnableAccessCodeResponse,
  launchId: string,
): EnableAccessCodeResponse {
  return {
    launch_id: launchId,
    access_code_disabled_at: raw.access_code_disabled_at ?? undefined,
  };
}

export { createIdempotencyKey };
